import { seoFaqs } from './faqs';
import { SOFTWARE_APPLICATION_PLACEHOLDERS } from '../config/softwareApplicationSchema.defaults';
import { SITE_NAME, SITE_URL } from '../config/site';
import { toPublishedDateTime } from '../utils/blogDates';

/** Matches the “Simple flow” steps on the home page (#how). */
const NOTRACK_SIMPLE_FLOW_HOW_TO_STEPS = [
  {
    name: 'Get your personalized starting plan',
    text: 'Based on your goal, lifestyle, and food habits.',
  },
  {
    name: 'Follow simple daily guidance',
    text: 'No calorie tracking—just clear direction for your meals and habits.',
  },
  {
    name: 'Check in daily',
    text: 'A quick check-in so the system can respond to your consistency.',
  },
  {
    name: 'AI adjusts your plan',
    text: 'Guidance updates based on your consistency and progress over time.',
  },
];

export function buildFaqSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: seoFaqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
}

export function buildHowToSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How Notrack works: simple flow for a Simple Diet Plan India',
    description:
      'A four-step flow for personalized guidance, daily structure, quick check-ins, and adaptive AI updates—built for Indian home food without calorie logging.',
    inLanguage: 'en-IN',
    totalTime: 'PT3M',
    step: NOTRACK_SIMPLE_FLOW_HOW_TO_STEPS.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    description:
      'Notrack helps you lose weight without tracking calories—a Simple Diet Plan India approach with Indian diet structure, habits, and daily guidance.',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    description:
      'Nutrition app for Indian users—Simple Diet Plan India guidance, weight loss without calorie tracking, and daily habit structure.',
  };
}

/**
 * SoftwareApplication schema for homepage (and reuse elsewhere if needed).
 * @param {Partial<typeof SOFTWARE_APPLICATION_PLACEHOLDERS & { url?: string }>} [overrides]
 */
export function buildSoftwareApplicationSchema(overrides = {}) {
  const merged = { ...SOFTWARE_APPLICATION_PLACEHOLDERS, ...overrides };
  const appUrl = merged.url || SITE_URL;

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${SITE_URL}/#softwareapplication`,
    name: merged.name,
    applicationCategory: merged.applicationCategory,
    operatingSystem: merged.operatingSystem,
    description: merged.description,
    url: appUrl,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
      availability: 'https://schema.org/PreOrder',
      description: 'Early access / waitlist; see website for founding offers.',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export { SOFTWARE_APPLICATION_PLACEHOLDERS };

/**
 * Article JSON-LD for blog posts. Pass merged `author` from BlogPostTemplate (name, schemaType, url).
 */
export function buildArticleSchema(post, options = {}) {
  const authorOpt = options.author;
  const datePublished = toPublishedDateTime(post.published);
  const dateModified = post.modified ? toPublishedDateTime(post.modified) : datePublished;

  let authorSchema;
  if (!authorOpt) {
    authorSchema = { '@type': 'Organization', name: SITE_NAME, url: SITE_URL };
  } else if (authorOpt.schemaType === 'Person') {
    authorSchema = {
      '@type': 'Person',
      name: authorOpt.name,
      ...(authorOpt.url ? { url: authorOpt.url } : {}),
    };
  } else {
    authorSchema = {
      '@type': 'Organization',
      name: authorOpt.name || SITE_NAME,
      url: authorOpt.url || SITE_URL,
    };
  }

  const pubBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const logoUrl = `${SITE_URL}${pubBase}/logo192.png`;

  const article = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${SITE_URL}/blog/${post.slug}#article`,
    headline: post.title,
    description: post.metaDescription,
    datePublished,
    dateModified,
    author: authorSchema,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
  };

  if (post.heroImage?.src) {
    article.image = [post.heroImage.src];
  }

  return article;
}
